import type { FC, ReactNode } from 'react'
import type { Category } from '@/types'

import Link from 'next/link'
import { cn } from '@/lib/utils'

interface TopicChipsProps {
    categories: Category[]
    activeSlug?: string | null
    label?: string
}

const chipClass = 'inline-flex h-10 shrink-0 items-center gap-2 rounded-full border px-4 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'

export const TopicChips: FC<TopicChipsProps> = ({ categories, activeSlug = null, label = 'Browse by topic' }): ReactNode => {
    if (categories.length === 0) return null

    return (
        <nav aria-label={label} className='-mx-5 overflow-x-auto px-5 no-scrollbar sm:mx-0 sm:px-0'>
            <ul className='flex gap-2 sm:flex-wrap'>
                <li>
                    <Link
                        href='/blog'
                        aria-current={activeSlug === null ? 'page' : undefined}
                        className={cn(
                            chipClass,
                            activeSlug === null
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-border bg-card text-foreground hover:border-foreground/30'
                        )}
                    >
                        All stories
                    </Link>
                </li>

                {categories.map(category => (
                    <li key={category.id}>
                        <Link
                            href={`/topics/${category.slug}`}
                            aria-current={activeSlug === category.slug ? 'page' : undefined}
                            className={cn(
                                chipClass,
                                activeSlug === category.slug
                                    ? 'border-primary bg-primary text-primary-foreground'
                                    : 'border-border bg-card text-foreground hover:border-foreground/30'
                            )}
                        >
                            <span>{category.name}</span>

                            <span
                                className={cn(
                                    'text-xs tabular-nums',
                                    activeSlug === category.slug ? 'text-primary-foreground/70' : 'text-muted-foreground'
                                )}
                            >
                                {category.postCount}
                            </span>
                        </Link>
                    </li>
                ))}
            </ul>
        </nav>
    )
}
